import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import V4Context from "sap/ui/model/odata/v4/Context";
import { showError, showSuccess } from "vamos/cockpit/base/handler/MessageToastHandler";
import { Article, DiscontinuedArticle, Product } from "vamos/cockpit/model/odata/VamosModel";
import Field from "sap/ui/mdc/Field";
import Table from "sap/m/Table";
import ListItem from "sap/ui/core/ListItem";
import { ProductHandler } from "vamos/cockpit/data/product/handler/ProductHandler";
import ValueHelp from "sap/ui/mdc/ValueHelp";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import NewArticleDialogController from "vamos/cockpit/data/article/controller/NewArticleDialog.controller";
import NewVariantDialogController from "vamos/cockpit/data/article/controller/NewVariantDialog.controller";
import SearchField, { SearchField$SearchEvent } from "sap/m/SearchField";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import { ResponseString } from "vamos/cockpit/model/api/vamos.private.api.types";

/**
 * @namespace vamos.cockpit.data.masterdata.bmecat.discontinueditems.controller
 * */
export default class ReplaceDiscontinuedArticleDialogController extends BaseTypeController {
    private dialog: Dialog;
    private _oDialogModel: JSONModel;
    private _query:string;

    private saveCallBack: () => void;

    onInit(): void {
        super.onInit();
        this.dialog = this.getView().byId("replaceDiscontinuedArticleDialog") as Dialog;
    }

    public openDialog(discontinuedArticles: any[], saveCallBack: () => void): void {
        this.saveCallBack = saveCallBack;
        this._oDialogModel = new JSONModel({
            articles: discontinuedArticles,
            currentIndex: 0,
            total: discontinuedArticles.length,
            articleSelected: false,
            currentArticleList: [],
            currentProduct: null,
            selectedArticleGuid: null,
            selectedArticleName: null,
            selectedProductGuid: null,
            alternativeProducts: [],
        });

        this.getView().setModel(this._oDialogModel, "replaceDialog");

        this._updateForCurrentArticle();
        this._resetSearchFields();
        this.dialog.open();
    }

    public async onAfterOpen() {
        const discontinuedArticle = this.getCurrentArticle();
        await this.setSelectedArticleGuid(discontinuedArticle.ProductDetail.ArticleGuid, discontinuedArticle.ProductDetail.ArticleNumber, discontinuedArticle.ProductDetail.ArticleName);
    }

    public onClosePopup(): void {
        this.dialog.close();
    }

    public async onSelectArticle(oEvent): void {
        if (!oEvent.getParameter("valid")) return;
        const productTable = this.byId("alternativeProductsTable") as Table;
        const binding = productTable.getBinding("items") as ODataListBinding;
        binding.filter([]);

        const sArticleGuid = oEvent.getSource().getValue() as string;
        const article = this.getSelectedArticle(oEvent);
        await this.setSelectedArticleGuid(sArticleGuid, article.Number, article.Name);
        this.setArticleValueHelpName(article);
    }

    private async setSelectedArticleGuid(articleGuid: string, articleNumber: string = null, articleName: string = null) {
        this._oDialogModel.setProperty("/selectedArticleGuid", articleGuid);
        this._oDialogModel.setProperty("/selectedArticleName", articleNumber + " - " + articleName);
        const productTable = this.byId("alternativeProductsTable") as Table;
        productTable.removeSelections(true);

        const searchField  = this.byId("alternativeProductsSearchField") as SearchField;
        searchField.setValue("");

        this.updateProductTableFilter(articleGuid,null);

        this._oDialogModel.setProperty("/selectedProductGuid", null);
        this._oDialogModel.setProperty("/alternativeProducts", []);
        this._oDialogModel.setProperty("/articleSelected", true);

        if (!articleGuid) return;

        await this.updateAlternativeProducts(articleGuid);
    }

    private updateProductTableFilter(sArticleGuid: string | null) {
        let variantFilter = new Filter([
            new Filter("ArticleGuid", FilterOperator.EQ, sArticleGuid),
            new Filter("IsDisabled", FilterOperator.EQ, false),
        ], true);
        const oVariantVH = this.byId("variant-vh") as ValueHelp;
        if (oVariantVH) {
            oVariantVH._oCustomFilter = sArticleGuid
                ? variantFilter
                : null;
        }
    }

    private getSelectedArticle(event): Article {
        const oArticleField = event.getSource();
        const oVH = this.byId(oArticleField.getValueHelp()) as ValueHelp;
        const jsonDelegate = oVH.getControlDelegate();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unused-vars,@typescript-eslint/no-unsafe-call
        const oArticleContext = jsonDelegate.getSelectedContext(oVH);
        const article = oArticleContext?.getObject() as Article;

        return article;
    }

    private setArticleValueHelpName(article: Article) {
        const articleField = this.getView().byId("replaceArticleField") as Field;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const sName = article.Number + " - " + article.Name;
        articleField.setAdditionalValue(sName);
    }

    private async updateAlternativeProducts(sArticleGuid: string): Promise<void> {
        const oFilter = new Filter([
            new Filter("ArticleGuid", FilterOperator.EQ, sArticleGuid),
            new Filter("IsDisabled", FilterOperator.EQ, "false"),
        ], true);

        const oModel = this.getModel() as ODataModel;
        const contexts = await oModel.bindList("/Products", undefined, [], [oFilter]).requestContexts(0, Infinity);

        const discontinuedArticle = this.getCurrentArticle();
        const products = contexts.map((c) => {
            const product = c.getObject() as Product
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
            product.Matching = ProductHandler.countSameProperties(discontinuedArticle.ProductDetail, product);
            return product;
        });
        this._oDialogModel.setProperty("/alternativeProducts", products);
        for (const product of products) {
            if (ProductHandler.hasSameProperties(discontinuedArticle.ProductDetail, product)) {

                await this.setSelectedItem(product.Guid);
                break;
            }
        }
    }

    private async setSelectedItem(productGuid: string): Promise<void> {
        const productTable = this.byId("alternativeProductsTable") as Table;
        for (const listProductItem of productTable.getItems()) {
            const odataContext = listProductItem.getBindingContext("replaceDialog") as V4Context;
            const listProduct = odataContext.getObject() as Product;

            if (listProduct.Guid == productGuid) {
                listProductItem.setSelected(true);
                const index = productTable.indexOfItem(listProductItem);
                this._oDialogModel.setProperty("/selectedProductGuid", productGuid);
                await productTable.scrollToIndex(index);
                break;
            }
        }
    }

    public onSelectAlternativeProduct(oEvent: any): void {
        const oListItem = oEvent.getParameter("listItem") as ListItem;
        if (!oListItem) {
            this._oDialogModel.setProperty("/selectedProductGuid", null);
            return;
        }
        const oContext = oListItem.getBindingContext("replaceDialog") as V4Context;
        const sGuid = oContext?.getProperty("Guid") as string;
        this._oDialogModel.setProperty("/selectedProductGuid", sGuid || null);
    }

    public async onNewVariantPress(): void {
        const view = await ViewHandler.getView("vamos.cockpit.data.article.view.NewVariantDialog", this.getComponent());
        const controller = view.getController() as NewVariantDialogController;

        const sPath = this.getKeyPredicateFromEntitySet("Article", this._oDialogModel.getProperty("/selectedArticleGuid") as string);
        const contextBinding: V4Context = this.getModel().bindContext(sPath);
        const article = await contextBinding.requestObject() as Article;

        await controller.openDialog(article, null, false, false, (product, openSupplierConditionDialog) => {
            this.onSaveNewVariantDone(product).then(() => {
            }).catch(() => {
            });
        });
    }

    public async onNewArticlePress(): Promise<void> {
        const view = await ViewHandler.getView("vamos.cockpit.data.article.view.NewArticleDialog", this.getComponent());
        const controller = view.getController() as NewArticleDialogController;
        controller.openDialog(false, (article: Article) => {
            this.onSaveNewArticleDone(article).then(() => {
            }).catch(() => {
            });
        });
    }

    private async onSaveNewArticleDone(article: Article): Promise<void> {
        await this.setSelectedArticleGuid(article.Guid, article.Number, article.Name);
        this.setArticleValueHelpName(article);
    }

    private async onSaveNewVariantDone(product: Product): Promise<void> {
        await this.updateAlternativeProducts(product.ArticleGuid);
        await this.setSelectedItem(product.Guid);
    }

    public async onSaveAndNext(): Promise<void> {
        const currentIndex = this._oDialogModel.getProperty("/currentIndex") as number;
        const selectedProductGuid = this._oDialogModel.getProperty("/selectedProductGuid") as string;

        const discontinuedArticle = this.getCurrentArticle();

        this.dialog.setBusy(true);
        try {
            const response = await ApiService.getInstance().postRestJsonDataWithoutMessage(`/discontinued-product/transfer/${discontinuedArticle.Guid}/${selectedProductGuid}`, {});
            this.saveCallBack();
            const total = this._oDialogModel.getProperty("/total") as number;
            const nextIndex = currentIndex + 1;
            if (nextIndex >= total) {
                this.dialog.close();
            } else {
                this._oDialogModel.setProperty("/currentIndex", nextIndex);
                this._updateForCurrentArticle();
                const discontinuedArticle = this.getCurrentArticle();
                await this.setSelectedArticleGuid(discontinuedArticle.ProductDetail.ArticleGuid, discontinuedArticle.ProductDetail.ArticleNumber, discontinuedArticle.ProductDetail.ArticleName);
            }
            if(response.success) {
                showSuccess(this.getText("ArticleTransferred"));
            } else {

                const reponseData = response.data as ResponseString;

                if (reponseData?.additionalInfo?.length > 0) {
                    showError(reponseData.additionalInfo.map((error: any) => error.Message).join("\n"));
                } else {
                    showError(this.getText("ArticleTransferredError"));
                }

            }
        } finally {
            this.dialog.setBusy(false);
        }
    }

    private getCurrentArticle(): DiscontinuedArticle | null {
        const articles = this._oDialogModel.getProperty("/articles") as any[];
        const currentIndex = this._oDialogModel.getProperty("/currentIndex") as number;
        return articles[currentIndex] as DiscontinuedArticle ?? null;
    }

    private _updateForCurrentArticle(): void {
        const discontinuedArticle = this.getCurrentArticle();
        const product = discontinuedArticle?.ProductDetail;

        this._oDialogModel.setProperty("/currentArticleList", product ? [product] : []);
        this._oDialogModel.setProperty("/currentProduct", product);
        this._oDialogModel.setProperty("/selectedArticleGuid", null);
        this._oDialogModel.setProperty("/articleSelected", false);
        this._oDialogModel.setProperty("/selectedArticleName", null);
        this._oDialogModel.setProperty("/selectedProductGuid", null);
        this._oDialogModel.setProperty("/alternativeProducts", []);

        this._resetSearchFields();
    }

    private _resetSearchFields(): void {
        const oArticleField = this.byId("replaceArticleField") as Field;
        if (oArticleField) {
            oArticleField.setValue(null);
            oArticleField.setAdditionalValue(null);
        }
        const oResultTable = this.byId("replacementSearchResultsTable") as Table;
        if (oResultTable) {
            oResultTable.removeSelections(true);
        }
    }

    private async onSearchAlternativeProducts(event: SearchField$SearchEvent) {
        this._query = event.getParameter("query");
        const articleGuid = this._oDialogModel.getProperty("/selectedArticleGuid");
        const searchFilters:Filter[] = [];


        if ( this._query ) {
            const normalFilters = ["ArticleNumber", "Name", "InnerLaminationName", "OuterLaminationName",
                "SealName", "SealColorName", "CarrierName", "GeometryName", "AdditionalEquipmentName"];
            normalFilters.forEach((path) => {
                searchFilters.push(
                    new Filter({
                        path: path,
                        operator: FilterOperator.Contains,
                        value1:  this._query ,
                        caseSensitive: false,
                    }),
                );
            });
        }
        const productTable = this.byId("alternativeProductsTable") as Table;
        const binding = productTable.getBinding("items") as ODataListBinding;
        binding.filter(searchFilters.length > 0 ? new Filter(searchFilters) : null, "Application");


        await this.updateAlternativeProducts(articleGuid as string);


    }




}